import { LaunchChangeKind, LaunchEmailRecipient } from './launchChange';

import type { LaunchChange, LaunchEmailChange } from './launchChange';
import type { useAmelioTranslations } from '@/hooks';
import type { TranslationKey } from '@/i18n-key';

type TranslateFn = ReturnType<typeof useAmelioTranslations>['t'];

const CHANGES_KEY_PREFIX = 'cycleEvaluationSchedule.launchSummary.changes' as const;
const SCHEDULE_KEY_PREFIX = 'cycleEvaluationSchedule.launchSummary.schedule' as const;

type ChangesKey = Extract<TranslationKey, `${typeof CHANGES_KEY_PREFIX}.${string}`>;
type ChangesSuffix = ChangesKey extends `${typeof CHANGES_KEY_PREFIX}.${infer Suffix}`
    ? Suffix
    : never;
type ScheduleKey = Extract<TranslationKey, `${typeof SCHEDULE_KEY_PREFIX}.${string}`>;

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
};

const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
    ...DATE_OPTIONS,
    hour: '2-digit',
    minute: '2-digit',
};

interface LaunchChangeText {
    i18nKey: ChangesKey;
    values: Record<string, string>;
    isAlreadyTriggered: boolean;
}

interface LaunchChangeTextParams {
    change: LaunchChange;
    calendarLocale: string;
    t: TranslateFn;
}

const toChangesKey = (suffix: ChangesSuffix): ChangesKey =>
    `${CHANGES_KEY_PREFIX}.${suffix}` as ChangesKey;

const formatMoment = ({
    value,
    calendarLocale,
    options,
    emptyLabel,
}: {
    value: string | undefined;
    calendarLocale: string;
    options: Intl.DateTimeFormatOptions;
    emptyLabel: string;
}): string => {
    const parsed = value ? new Date(value) : undefined;

    if (!parsed || Number.isNaN(parsed.getTime())) {
        return emptyLabel;
    }

    return parsed.toLocaleString(calendarLocale, options);
};

/**
 * A sentence closing on an abbreviation keeps that abbreviation's period as its own; appending the
 * template's period too is what renders "05:04 p.m.." in the mockup.
 */
const resolveStepDateKey = (formattedTo: string) =>
    formattedTo.endsWith('.')
        ? toChangesKey('stepDateChangedAbbreviated')
        : toChangesKey('stepDateChanged');

const readAudienceModeLabel = ({
    isEntireCompany,
    t,
}: {
    isEntireCompany: boolean;
    t: TranslateFn;
}) =>
    isEntireCompany
        ? t(toChangesKey('audienceEntireCompany'))
        : t(toChangesKey('audienceTargeted'));

const readRecipientLabel = ({ change, t }: { change: LaunchEmailChange; t: TranslateFn }) => {
    const isEmployee = change.recipient === LaunchEmailRecipient.EMPLOYEE;
    const suffix = isEmployee ? 'audienceEmployees' : 'audienceManagers';

    return t(`${SCHEDULE_KEY_PREFIX}.${suffix}` satisfies ScheduleKey);
};

const toLaunchChangeText = ({
    change,
    calendarLocale,
    t,
}: LaunchChangeTextParams): LaunchChangeText => {
    const emptyLabel = t(toChangesKey('noDate'));
    const isAlreadyTriggered = 'isAlreadyTriggered' in change && change.isAlreadyTriggered;
    const asDate = (value: string | undefined) =>
        formatMoment({ value, calendarLocale, options: DATE_OPTIONS, emptyLabel });
    const asDateTime = (value: string | undefined) =>
        formatMoment({ value, calendarLocale, options: DATE_TIME_OPTIONS, emptyLabel });

    if (change.kind === LaunchChangeKind.NAME_CHANGED) {
        return {
            i18nKey: toChangesKey(change.kind),
            values: { from: change.from, to: change.to },
            isAlreadyTriggered,
        };
    }

    if (
        change.kind === LaunchChangeKind.CYCLE_START_CHANGED ||
        change.kind === LaunchChangeKind.CYCLE_END_CHANGED
    ) {
        return {
            i18nKey: toChangesKey(change.kind),
            values: { from: asDate(change.from), to: asDate(change.to) },
            isAlreadyTriggered,
        };
    }

    if (change.kind === LaunchChangeKind.AUDIENCE_CHANGED) {
        return {
            i18nKey: toChangesKey(change.kind),
            values: {
                from: readAudienceModeLabel({ isEntireCompany: change.wasEntireCompany, t }),
                to: readAudienceModeLabel({ isEntireCompany: change.isEntireCompany, t }),
            },
            isAlreadyTriggered,
        };
    }

    if (change.kind === LaunchChangeKind.AUDIENCE_FILTERS_CHANGED) {
        return { i18nKey: toChangesKey(change.kind), values: {}, isAlreadyTriggered };
    }

    if (change.kind === LaunchChangeKind.EVALUATION_ADDED) {
        return {
            i18nKey: toChangesKey(change.kind),
            values: { name: t(change.labelKey), date: asDate(change.scheduledAt) },
            isAlreadyTriggered,
        };
    }

    if (
        change.kind === LaunchChangeKind.EVALUATION_ADDED_NO_DATE ||
        change.kind === LaunchChangeKind.EVALUATION_REMOVED ||
        change.kind === LaunchChangeKind.COMMENT_REPLIES_ENABLED ||
        change.kind === LaunchChangeKind.COMMENT_REPLIES_DISABLED
    ) {
        return {
            i18nKey: toChangesKey(change.kind),
            values: { name: t(change.labelKey) },
            isAlreadyTriggered,
        };
    }

    if (
        change.kind === LaunchChangeKind.TEMPLATE_CHANGED ||
        change.kind === LaunchChangeKind.TEMPLATE_SET
    ) {
        return {
            i18nKey: toChangesKey(change.kind),
            values: { name: t(change.labelKey), from: change.from, to: change.to },
            isAlreadyTriggered,
        };
    }

    if (change.kind === LaunchChangeKind.STEP_DATE_CHANGED) {
        const to = asDateTime(change.to);

        return {
            i18nKey: resolveStepDateKey(to),
            values: { step: t(change.labelKey), from: asDateTime(change.from), to },
            isAlreadyTriggered,
        };
    }

    return {
        i18nKey: toChangesKey(change.kind),
        values: { step: t(change.labelKey), audience: readRecipientLabel({ change, t }) },
        isAlreadyTriggered,
    };
};

export type { LaunchChangeText };
export { DATE_OPTIONS, DATE_TIME_OPTIONS, toChangesKey, toLaunchChangeText };
